import { HttpResponse, http, type HttpHandler } from 'msw';
import { BFF_ENDPOINTS } from '../../api/endpoints/BffEndpoints';
import type { ValidateEmailPayload } from '../../api/models/bff/Registration';
import { toMSWPattern } from '../utils/generic';
import type { HTTP_METHODS } from './constants/generic';
import { HTTP_METHODS_VALUES } from './constants/generic';

interface HandlerMapping {
  pattern: string;
  key: string;
  method?: HTTP_METHODS;
}

export interface HandlerWithMapping {
  handler: HttpHandler;
  mapping: HandlerMapping;
}

export const ENDPOINTS_KEYS = {
  VALIDATE_EMAIL: 'VALIDATE_EMAIL',
};

/**
 * Invalid email domains for testing error scenarios
 */
const INVALID_DOMAINS = ['test.com', 'fail.com', 'blacklist.org'];

/**
 * Check if email is valid based on simple rules
 */
function isEmailValid(email: string): boolean {
  const domain = email.split('@')[1];
  return !!domain && !INVALID_DOMAINS.includes(domain);
}

export function createRegistrationHandlers(baseUrl: string): HandlerWithMapping[] {
  const handlers: HandlerWithMapping[] = [
    {
      handler: http.post(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.VALIDATE_EMAIL)}`, async ({ request }) => {
        const body = await request.json() as ValidateEmailPayload;
        const { email } = body;

        // Return 400 for invalid emails
        if (!isEmailValid(email)) {
          return HttpResponse.json(
            {
              code: 'INVALID_EMAIL_DOMAIN',
              message: `Email domain is not allowed: ${email.split('@')[1]}`,
              translationKey: 'errors.registration.invalidEmailDomain',
              source: 'VALIDATION',
              details: [
                {
                  name: 'email',
                  reason: 'INVALID_DOMAIN',
                },
              ],
            },
            { status: 400 },
          );
        }

        // Return 202 with no body for valid emails
        return new HttpResponse(null, { status: 202 });
      }),
      mapping: {
        pattern: 'identities/registrations',
        key: ENDPOINTS_KEYS.VALIDATE_EMAIL,
        method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
      },
    },
  ];
  return handlers;
}
